"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
/**
 * Returns true if the given value is a number.
 * @private
 * @param value Any value.
 * @returns {boolean} True if the given value is a number.
 */
function isValueNumber(value) {
    return typeof value === 'number';
}
/**
 * Returns true if the given value is an integer.
 * @param value Any value.
 */
function isValueInteger(value) {
    return isValueNumber(value) && Number.isFinite(value) && Math.floor(value) === value;
}
/**
 * Assertion-like functions for checking invariants.
 */
var check;
(function (check) {
    /**
     * Checks whether the given value is a Boolean.
     * @param name The name of the value being checked.
     * @param value The value to check.
     */
    function isBoolean(name, value) {
        if (value !== true && value !== false) {
            throw new Error(`Expected ${name} = ${JSON.stringify(value)} to be a Boolean.`);
        }
    }
    check.isBoolean = isBoolean;
    /**
     * Checks whether the given value is a number.
     * @param name The name of the value being checked.
     * @param value The value to check.
     */
    function isNumber(name, value) {
        if (!isValueNumber(value)) {
            throw new Error(`Expected ${name} = ${JSON.stringify(value)} to be a number.`);
        }
    }
    check.isNumber = isNumber;
    /**
     * Checks whether the given value is an integer.
     * @param name The name of the value being checked.
     * @param value The value to check.
     */
    function isInteger(name, value) {
        if (!isValueInteger(value)) {
            throw new Error(`Expected ${name} = ${JSON.stringify(value)} to be an integer.`);
        }
    }
    check.isInteger = isInteger;
    /**
     * Checks whether the given value is a positive number.
     * @param name The name of the value being checked.
     * @param value The value to check.
     */
    function isPositive(name, value) {
        check.isNumber(name, value);
        if (!(value > 0)) {
            throw new Error(`Expected ${name} = ${JSON.stringify(value)} to be positive.`);
        }
    }
    check.isPositive = isPositive;
    /**
     * Checks whether the given value is a non-negative integer.
     * @param name The name of the value being checked.
     * @param value The value to check.
     */
    function isNonNegativeInteger(name, value) {
        check.isInteger(name, value);
        if (value < 0) {
            throw new Error(`Expected ${name} = ${JSON.stringify(value)} to be non-negative.`);
        }
    }
    check.isNonNegativeInteger = isNonNegativeInteger;
    /**
     * Checks whether the given value is a point (an array of two or three numbers).
     * @param name The name of the value being checked.
     * @param value The value to check.
     */
    function isPoint(name, value) {
        check.isArray(name, value, [2, 3], check.isNumber);
    }
    check.isPoint = isPoint;
    /**
     * Checks whether the given value is a 2D point.
     * @param name The name of the value being checked.
     * @param value The value to check.
     */
    function is2DPoint(name, value) {
        check.isArray(name, value, 2, check.isNumber);
    }
    check.is2DPoint = is2DPoint;
    /**
     * Checks whether the given value is a 3D point.
     * @param name The name of the value being checked.
     * @param value The value to check.
     */
    function is3DPoint(name, value) {
        check.isArray(name, value, 3, check.isNumber);
    }
    check.is3DPoint = is3DPoint;
    /**
     * Checks whether the given value is a number within an expected range.
     * @param name The name of the value being checked.
     * @param value The value to check.
     * @param expectedMin The minimum expected value.
     * @param expectedMax The maximum expected value.
     */
    function isInRange(name, value, expectedMin, expectedMax) {
        check.isNumber(name, value);
        if (!(value >= expectedMin)) {
            throw new Error(`Expected ${name} = ${JSON.stringify(value)} to be no less than ${expectedMin}.`);
        }
        if (!(value <= expectedMax)) {
            throw new Error(`Expected ${name} = ${JSON.stringify(value)} to be no more than ${expectedMax}.`);
        }
    }
    check.isInRange = isInRange;
    /**
     * Checks whether the given value is one of the expected values.
     * @param name The name of the value being checked.
     * @param value The value to check.
     * @param expectedValues An array of permissible values.
     */
    function isAmong(name, value, expectedValues) {
        if (expectedValues.indexOf(value) < 0) {
            throw new Error(`Expected ${name} = ${JSON.stringify(value)} to be among ${JSON.stringify(expectedValues)}.`);
        }
    }
    check.isAmong = isAmong;
    /**
     * Checks whether the given array contains the specified value.
     * @param name The name of the array being checked.
     * @param array The array to check.
     * @param value The value to look for in the array.
     */
    function contains(name, array, value) {
        if (array.indexOf(value) < 0) {
            throw new Error(`Expected ${name} to contain ${JSON.stringify(value)}.`);
        }
    }
    check.contains = contains;
    /**
     * Checks whether the given value equals the expected value. Uses `===` to test for
     * shallow equality.
     * @param name The name of the value being checked.
     * @param value The value to check.
     * @param expectedValue The expected value.
     */
    function equals(name, value, expectedValue) {
        if (!(value === expectedValue)) {
            throw new Error(`Expected ${name} = ${JSON.stringify(value)} to equal ${JSON.stringify(expectedValue)}.`);
        }
    }
    check.equals = equals;
    /**
     * Checks whether the given value does not equal the expected value. Uses `!==` to test for
     * shallow inequality.
     * @param name The name of the value being checked.
     * @param value The value to check.
     * @param notExpectedValue Not the expected value.
     */
    function notEquals(name, value, notExpectedValue) {
        if (!(value !== notExpectedValue)) {
            throw new Error(`Expected ${name} = ${JSON.stringify(value)} not to equal ${JSON.stringify(notExpectedValue)}.`);
        }
    }
    check.notEquals = notEquals;
    /**
     * Checks whether the given value is an instance of the specified type.
     * @param name The name of the value being checked.
     * @param value The value to check.
     * @param type The expected type of value.
     */
    function isInstanceOf(name, value, type) {
        if (!(value instanceof type)) {
            throw new Error(`Expected ${name} to be an instance of ${type.name}.`);
        }
    }
    check.isInstanceOf = isInstanceOf;
    /**
     * Checks whether the given value has a property with the specified name.
     * @param name The name of the value being checked.
     * @param value The value to check.
     * @param propertyName The property name that is expected.
     * @param checkPropertyValueFunction The function used to check the
     * property value.
     */
    function hasProperty(name, value, propertyName, checkPropertyValueFunction) {
        if (value[propertyName] === undefined) {
            throw new Error(`Expected ${name} to have a property named ${propertyName}.`);
        }
        if (checkPropertyValueFunction) {
            checkPropertyValueFunction(`${name}.${propertyName}`, value[propertyName]);
        }
    }
    check.hasProperty = hasProperty;
    /**
     * Checks whether the given value is an array and has the expected length, then checks each of
     * its items using the provided function.
     * @param name The name of the array being checked.
     * @param value The array to check.
     * @param expectedLength The expected length of the array, or an array
     * containing the minimum and maximum acceptable length of the array, or undefined if any
     * length is acceptable.
     * @param checkItemFunction The function used to check each item in the array.
     * @param args Anything else gets passed to checkItemFunction.
     */
    function isArray(name, value, expectedLength, checkItemFunction, ...args) {
        if (!Array.isArray(value)) {
            throw new Error(`Expected ${name} to be an array.`);
        }
        if (isValueNumber(expectedLength) && value.length !== expectedLength) {
            throw new Error(`Expected ${name}.length = ${value.length} to be ${expectedLength}.`);
        }
        if (Array.isArray(expectedLength)
            && (value.length < expectedLength[0] || value.length > expectedLength[1])) {
            throw new Error(`Expected ${name}.length = ${value.length} to be between ${expectedLength[0]} and ${expectedLength[1]}.`);
        }
        if (checkItemFunction) {
            value.forEach((item, i) => {
                checkItemFunction(`${name}[${i}]`, item, i, ...args);
            });
        }
    }
    check.isArray = isArray;
    /**
     * Checks whether the given value is an array and has the expected length, then checks whether
     * each of its items is also an array of the expected item length, and finally checks each of
     * the nested items using the provided function.
     * @param name The name of the array being checked.
     * @param value The array to check.
     * @param expectedLength The expected length of the array, or an array
     * containing the minimum and maximum acceptable length of the array, or undefined if any
     * length is acceptable.
     * @param expectedItemLength The expected length of each item's array,
     * or an array containing the minimum and maximum acceptable length, or undefined.
     * @param checkItemItemFunction The function used to check each item in the array's
     * subarrays.
     * @param args Anything else gets passed to checkItemItemFunction.
     */
    function isArrayOfArrays(name, value, expectedLength, expectedItemLength, checkItemItemFunction, ...args) {
        check.isArray(name, value, expectedLength, (itemName, itemValue, itemIndex) => check.isArray(itemName, itemValue, expectedItemLength, checkItemItemFunction, ...args, itemIndex));
    }
    check.isArrayOfArrays = isArrayOfArrays;
})(check = exports.check || (exports.check = {}));
//# sourceMappingURL=check.js.map